/** Exhibitor showcase — 3-column layout with rotating exhibitor slides. */

import { useEffect, useState } from 'react';
import type { HDEAssets } from './types';
import { ScrollReveal } from './components/ScrollReveal';
import { HDEDesktopFrame } from './components/HDEDesktopFrame';

const ROTATE_INTERVAL_MS = 1500;

/** HDE serif letters SVG overlay (top-left of each slide). */
const HdeLettersSvg = ({
    className,
    style,
}: {
    className?: string;
    style?: React.CSSProperties;
}) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="180"
        height="233"
        viewBox="0 0 180 233"
        fill="none"
        className={className}
        style={{ width: '13.5625rem', height: 'auto', ...style }}
    >
        <path
            d="M105.122 119.913L105.121 119.917C105.737 119.917 106.167 120.524 105.953 121.097L95.6827 148.683C95.5747 148.97 95.2976 149.162 94.9873 149.162H94.6173C94.217 149.162 93.89 148.848 93.8776 148.453C93.7459 144.148 91.791 121.68 65.2762 121.68H48.1565C44.4993 121.68 40.9515 123.133 38.5147 125.83C36.1237 128.477 34.2785 132.698 34.2784 139.42V170.022H50.7095C73.4897 170.022 76.7537 154.363 77.1028 152.131C77.1264 151.978 77.2593 151.867 77.4159 151.867H77.884C78.0682 151.867 78.2138 152.02 78.2013 152.202L75.5303 190.209C75.5192 190.374 75.3808 190.502 75.2132 190.502H74.1908C74.0204 190.502 73.8804 190.367 73.8735 190.199C73.7737 187.88 72.2941 172.998 50.8455 172.998H34.2784V206.717C34.2784 211.508 34.2575 215.322 34.4805 218.362C34.4875 218.414 34.493 218.464 34.4999 218.516C35.2702 224.583 40.2436 229.29 46.4055 229.862C51.0823 230.296 57.5642 230.256 66.5437 230.256C97.5597 230.256 102.532 203.514 102.985 200.57C103.008 200.418 103.141 200.308 103.296 200.308H103.819C104.006 200.308 104.151 200.465 104.133 200.648L101.207 231.303C101.134 232.067 100.484 232.652 99.707 232.652H2.15966C1.90619 232.652 1.70125 232.449 1.70122 232.199V231.325C1.70122 231.069 1.9117 230.864 2.16934 230.872C4.52161 230.942 14.9241 230.373 14.9241 214.81V139.418C14.9241 121.788 2.236 121.361 0.257692 121.402C0.116414 121.405 3.96727e-05 121.294 0 121.154V120.16C0 120.024 0.112196 119.913 0.250715 119.913H105.122Z"
            fill="#F4F4F4"
        />
        <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M48.9516 0C49.0901 0 49.2023 0.110856 49.2023 0.247721V1.24149C49.2023 1.38106 49.0861 1.49189 48.9448 1.48921C46.9666 1.44952 34.2785 1.87522 34.2784 19.505V53.9926H84.0792V19.505C84.0792 1.87522 71.391 1.44815 69.4128 1.48921C69.2715 1.49193 69.1551 1.38109 69.1551 1.24149V0.247721C69.1551 0.110886 69.2673 4.87455e-05 69.4058 0H115.749C145.56 0 179.015 15.9256 179.015 56.1552C179.015 96.3847 153.433 112.739 114.549 112.739H71.3163C71.0628 112.739 70.8577 112.536 70.8577 112.286V111.413C70.8577 111.157 71.0683 110.951 71.326 110.959C73.6784 111.031 84.0805 110.46 84.0805 94.897V56.3689H34.2798V94.897C34.2798 110.46 44.6819 111.029 47.0343 110.959C47.292 110.953 47.5027 111.158 47.5027 111.413V112.286C47.5027 112.536 47.2975 112.739 47.044 112.739H2.15966C1.90617 112.739 1.70122 112.536 1.70122 112.286V111.413C1.70122 111.157 1.9117 110.951 2.16934 110.959C4.52161 111.031 14.9241 110.46 14.9241 94.897V19.505C14.9241 1.87529 2.236 1.44815 0.257692 1.48921C0.116395 1.49195 9.3018e-06 1.3811 0 1.24149V0.247721C1.49214e-05 0.110865 0.112205 1.47299e-05 0.250715 0H48.9516ZM106.55 2.07783C105.424 2.07788 104.51 2.97991 104.51 4.09406V54.3171C104.51 82.9582 104.567 100.8 106.211 104.088C107.832 107.331 108.385 110.234 115.219 110.234C137.089 110.234 159.41 98.7605 159.41 56.4345C159.41 14.108 130.1 2.07785 119.277 2.07783H106.55Z"
            fill="#F4F4F4"
        />
    </svg>
);

/** ArchiPro logo badge (bottom-right of each slide). */
const ByArchiProBadge = ({ isMobile = false }: { isMobile?: boolean }) => {
    if (isMobile) {
        return (
            <div className="absolute bottom-4 right-4 flex items-center gap-[0.2rem]">
                <span className="mt-3 font-sans text-[0.26rem] font-medium uppercase leading-[0.9] text-[#eee]">
                    By ArchiPro
                </span>
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="102"
                    height="75"
                    viewBox="0 0 102 75"
                    fill="none"
                    style={{ width: '3rem', height: 'auto' }}
                >
                    <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M100.98 74.2446C96.4708 64.9874 91.9676 55.733 87.467 46.4866C87.3984 46.3339 87.2831 46.1544 87.0938 46.0499C86.9044 45.9455 86.6931 45.9187 86.4434 45.9187H70.8068C79.5334 38.0196 85.1015 26.8446 85.4582 14.3973C85.4774 13.6982 84.8957 13.125 84.1767 13.125H71.2019C71.2019 30.6375 56.6575 44.8366 38.7129 44.8366H34.6323C39.6816 34.4517 44.7228 24.0669 49.7585 13.6768C49.8271 13.5134 49.9341 13.3419 50.1262 13.2402C50.3183 13.1384 50.5241 13.1277 50.7821 13.1277H71.2403L71.2211 13.0794L63.8776 0V0.00535714H36.8496C36.6657 0.00535714 36.5011 0.01875 36.3556 0.0937499C36.2102 0.171428 36.1251 0.297321 36.0702 0.420535C28.3397 16.3312 20.601 32.2634 12.8567 48.2169C8.6416 56.8901 4.4292 65.566 0.21405 74.2446C0.16191 74.341 0.123491 74.4428 0.0795828 74.5526C0.0548847 74.6169 0.0274424 74.6838 0 74.7535H19.5993C19.7558 74.7669 19.9149 74.7321 20.0439 74.6624C20.1729 74.5901 20.2634 74.4829 20.2964 74.3624C22.8924 68.9972 25.4802 63.6562 28.0626 58.3365C28.1312 58.1892 28.2327 58.0365 28.4083 57.9455C28.584 57.8544 28.7376 57.849 29.0038 57.849H72.2036C72.4725 57.849 72.607 57.8598 72.7771 57.9455C72.9473 58.0339 73.0406 58.1705 73.1147 58.3178C75.7025 63.6348 78.2821 68.9544 80.8507 74.2713C80.9028 74.3758 80.9961 74.5365 81.147 74.6196C81.298 74.7026 81.4324 74.7133 81.6684 74.7187H101.194L100.98 74.2392V74.2446Z"
                        fill="#EEEEEE"
                    />
                </svg>
            </div>
        );
    }

    return (
        <div className="absolute bottom-[2.5rem] right-[2.5rem] flex items-center gap-[0.4rem]">
            <span className="mt-[1.8rem] font-sans text-[0.625rem] font-medium uppercase leading-[0.9] text-[#eee]">
                By ArchiPro
            </span>
            <svg
                xmlns="http://www.w3.org/2000/svg"
                width="102"
                height="75"
                viewBox="0 0 102 75"
                fill="none"
            >
                <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M100.98 74.2446C96.4708 64.9874 91.9676 55.733 87.467 46.4866C87.3984 46.3339 87.2831 46.1544 87.0938 46.0499C86.9044 45.9455 86.6931 45.9187 86.4434 45.9187H70.8068C79.5334 38.0196 85.1015 26.8446 85.4582 14.3973C85.4774 13.6982 84.8957 13.125 84.1767 13.125H71.2019C71.2019 30.6375 56.6575 44.8366 38.7129 44.8366H34.6323C39.6816 34.4517 44.7228 24.0669 49.7585 13.6768C49.8271 13.5134 49.9341 13.3419 50.1262 13.2402C50.3183 13.1384 50.5241 13.1277 50.7821 13.1277H71.2403L71.2211 13.0794L63.8776 0V0.00535714H36.8496C36.6657 0.00535714 36.5011 0.01875 36.3556 0.0937499C36.2102 0.171428 36.1251 0.297321 36.0702 0.420535C28.3397 16.3312 20.601 32.2634 12.8567 48.2169C8.6416 56.8901 4.4292 65.566 0.21405 74.2446C0.16191 74.341 0.123491 74.4428 0.0795828 74.5526C0.0548847 74.6169 0.0274424 74.6838 0 74.7535H19.5993C19.7558 74.7669 19.9149 74.7321 20.0439 74.6624C20.1729 74.5901 20.2634 74.4829 20.2964 74.3624C22.8924 68.9972 25.4802 63.6562 28.0626 58.3365C28.1312 58.1892 28.2327 58.0365 28.4083 57.9455C28.584 57.8544 28.7376 57.849 29.0038 57.849H72.2036C72.4725 57.849 72.607 57.8598 72.7771 57.9455C72.9473 58.0339 73.0406 58.1705 73.1147 58.3178C75.7025 63.6348 78.2821 68.9544 80.8507 74.2713C80.9028 74.3758 80.9961 74.5365 81.147 74.6196C81.298 74.7026 81.4324 74.7133 81.6684 74.7187H101.194L100.98 74.2392V74.2446Z"
                    fill="#EEEEEE"
                />
            </svg>
        </div>
    );
};

interface ExhibitorGridProps {
    exhibitors: HDEAssets['exhibitors'];
    onRegister?: () => void;
    showRegisterButton?: boolean;
    isMobile?: boolean;
    sectionHeadingDesktop?: string;
    sectionHeadingMobile?: string;
    meetLabel?: string;
    atLabel?: string;
    eventWordOne?: string;
    eventWordTwo?: string;
    eventWordThree?: string;
}

export const ExhibitorGrid = ({
    exhibitors,
    onRegister,
    showRegisterButton = true,
    isMobile = false,
    sectionHeadingDesktop = 'EXHIBITORS',
    sectionHeadingMobile = 'Exhibitors',
    meetLabel = 'MEET',
    atLabel = 'at',
    eventWordOne = 'HOME',
    eventWordTwo = 'DESIGN',
    eventWordThree = 'EVENING',
}: ExhibitorGridProps) => {
    const [activeIndex, setActiveIndex] = useState(0);

    useEffect(() => {
        const timer = setInterval(() => {
            setActiveIndex((prev) => (prev + 1) % exhibitors.length);
        }, ROTATE_INTERVAL_MS);
        return () => clearInterval(timer);
    }, [exhibitors.length]);

    if (isMobile) {
        return (
            <section
                data-testid="exhibitor-grid"
                className="relative w-full bg-white px-[1.5rem] pb-[1.25rem] pt-[2.75rem]"
            >
                <h2 className="font-sans text-[1.25rem] font-medium uppercase leading-[0.9] text-[#141414]">
                    {sectionHeadingMobile}
                </h2>
                {/* Rotating exhibitor slide — all slides stacked, toggled via opacity */}
                <div className="relative mt-[1.75rem] aspect-[51.75/67] w-full shrink-0 overflow-hidden">
                    {exhibitors.map((exhibitor, index) => (
                        <div
                            key={index}
                            className="absolute inset-0"
                            style={{
                                opacity: index === activeIndex ? 1 : 0,
                            }}
                            aria-hidden={index !== activeIndex}
                        >
                            {exhibitor.image && (
                                <img
                                    src={exhibitor.image}
                                    alt={exhibitor.name}
                                    className="absolute inset-0 h-full w-full object-cover"
                                />
                            )}
                            <div className="absolute inset-0 flex flex-col items-center justify-center p-4">
                                <p className="w-full text-center font-sans text-5 font-medium uppercase leading-[0.9] text-white">
                                    {exhibitor.name}
                                </p>
                            </div>
                        </div>
                    ))}
                    <div className="absolute left-4 top-4">
                        <HdeLettersSvg
                            style={{ width: '5.75rem', height: 'auto' }}
                        />
                    </div>
                    <ByArchiProBadge isMobile={true} />
                </div>
                <button
                    type="button"
                    onClick={onRegister}
                    className={`mt-[1.75rem] flex h-[3.25rem] w-full items-center justify-center rounded-full bg-[#141414] ${showRegisterButton ? '' : 'pointer-events-none invisible'}`}
                >
                    <span className="text-[0.9375rem] text-white">
                        Register Now — Free
                    </span>
                </button>
            </section>
        );
    }

    return (
        <section
            data-testid="exhibitor-grid"
            className="relative w-full bg-white pt-[7.625rem]"
        >
            <HDEDesktopFrame className="relative">
                {/* EXHIBITORS heading — pinned to page edge, top-aligned with slider */}
                <h2 className="absolute left-[3.125rem] top-[8rem] font-sans text-[2.854rem] font-medium uppercase leading-[0.9] tracking-[-0.01rem] text-black">
                    {sectionHeadingDesktop}
                </h2>

                {/* Centred row: MEET | slide | right column */}
                <div className="mx-auto flex max-w-[104.375rem] items-stretch">
                    {/* Left column: MEET vertically centred */}
                    <div className="flex min-w-0 flex-1 flex-col justify-center">
                        <ScrollReveal animation="fade-left" duration={500}>
                            <p className="pl-[8.5rem] font-sans text-[2.887rem] font-medium leading-[0.9] text-black">
                                {meetLabel}
                            </p>
                        </ScrollReveal>
                    </div>

                    {/* Rotating exhibitor slide — all slides stacked, toggled via opacity */}
                    <div
                        className="relative shrink-0 overflow-hidden"
                        style={{
                            width: '51.75rem',
                            height: '67rem',
                            backgroundColor: '#c4c4c4',
                        }}
                    >
                        {exhibitors.map((exhibitor, index) => (
                            <div
                                key={index}
                                className="absolute inset-0"
                                style={{
                                    opacity: index === activeIndex ? 1 : 0,
                                }}
                                aria-hidden={index !== activeIndex}
                            >
                                {exhibitor.image && (
                                    <img
                                        src={exhibitor.image}
                                        alt={exhibitor.name}
                                        className="absolute inset-0 h-full w-full object-cover"
                                    />
                                )}
                                <div className="absolute inset-0 flex flex-col items-center justify-center p-8">
                                    <p className="w-full text-center font-sans text-[2.887rem] font-medium uppercase leading-[0.9] text-white">
                                        {exhibitor.name}
                                    </p>
                                </div>
                            </div>
                        ))}
                        <div className="absolute left-7 top-8">
                            <HdeLettersSvg />
                        </div>
                        <ByArchiProBadge />
                    </div>

                    {/* Right column: at —— HOME/DESIGN/EVENING + CTA */}
                    <div className="flex min-w-0 flex-1 flex-col items-end justify-center">
                        {/* "at" + dash inline with HOME DESIGN EVENING */}
                        <ScrollReveal
                            animation="fade-right"
                            duration={500}
                            delay={150}
                        >
                            <div className="mt-[12.5rem] flex items-start gap-[0.75rem]">
                                <div className="mt-[0.375rem] flex items-center gap-[0.75rem]">
                                    <p className="font-ghost text-[2.887rem] italic leading-[0.9] tracking-[-0.087rem] text-black">
                                        {atLabel}
                                    </p>
                                    <div
                                        className="h-[0.45rem] w-[3.25rem] bg-black"
                                        style={{
                                            transform:
                                                'translate(1.15rem, 0.15rem)',
                                        }}
                                    />
                                </div>
                                <div className="mr-4 flex flex-col items-end font-display text-[3rem] font-bold uppercase leading-[1.1] text-black">
                                    <p>{eventWordOne}</p>
                                    <p>{eventWordTwo}</p>
                                    <p>{eventWordThree}</p>
                                </div>
                            </div>
                        </ScrollReveal>

                        <ScrollReveal
                            animation="fade-up"
                            duration={400}
                            delay={400}
                        >
                            <button
                                type="button"
                                onClick={onRegister}
                                className={`mt-12 flex h-[3.125rem] items-center justify-center rounded-full border border-black bg-[#1e1e1e] px-[2.234rem] transition-colors hover:bg-gray-700 active:scale-[0.97] ${showRegisterButton ? '' : 'pointer-events-none invisible'}`}
                            >
                                <span className="whitespace-nowrap text-[1.058rem] font-normal leading-[1.713rem] text-white">
                                    Register Now – Free
                                </span>
                            </button>
                        </ScrollReveal>
                    </div>
                </div>
            </HDEDesktopFrame>
        </section>
    );
};
